import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, Loader2, KeyRound } from "lucide-react";

export default function ChangePassword({ forced = false }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("New password must be at least 8 characters.");
    if (next !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: current, new_password: next });
      setUser(data);
      navigate(data.role === "tech" ? "/tech" : data.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8] font-body px-6" data-testid="change-password-page">
      <div className="w-full max-w-md bg-white border border-[#E5E5E5] rounded-2xl p-8 shadow-sm">
        <div className="flex items-center gap-2">
          <span className="h-9 w-9 rounded-xl bg-[#114A31] flex items-center justify-center">
            <Leaf className="h-5 w-5 text-white" />
          </span>
          <span className="font-heading text-lg font-semibold text-[#1C1C1C]">ScorpPath</span>
        </div>
        <h1 className="mt-6 font-heading text-2xl font-medium text-[#1C1C1C] flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-[#114A31]" /> {forced ? "Set your password" : "Change password"}
        </h1>
        <p className="mt-2 text-sm text-[#595D5A]">
          {forced ? "For security, please set a new password before continuing." : "Update your account password."}
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-[#1C1C1C]">Current password</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required
              data-testid="cp-current" className="mt-1.5 rounded-xl border-[#E5E5E5] py-6" />
          </div>
          <div>
            <Label className="text-[#1C1C1C]">New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required
              data-testid="cp-new" className="mt-1.5 rounded-xl border-[#E5E5E5] py-6" />
          </div>
          <div>
            <Label className="text-[#1C1C1C]">Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
              data-testid="cp-confirm" className="mt-1.5 rounded-xl border-[#E5E5E5] py-6" />
          </div>
          {error && <p className="text-sm text-[#EF4444]" data-testid="cp-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="cp-submit"
            className="w-full rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white py-6">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
